import { Checkbox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import DepartmentForm, {
  DepartmentFormData,
  DepartmentFormHandle,
  departmentFormInitialValues,
  DepartmentRepeatField,
  DepartmentRepeatFieldLocks,
} from 'components/forms/department/DepartmentForm';
import panic from 'errors/panic';
import useLocalStorage from 'hooks/use-local-storage';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCurrentDepartmentText, useDepartmentText } from 'model/DepartmentTerminology';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DEPARTMENTS_PAGE_PATH, EDIT_DEPARTMENT_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { DEPARTMENT_REDIRECT_AFTER_SAVE } from 'env';

const initialFieldLocks: DepartmentRepeatFieldLocks = {
  organizationId: true,
  hierarchyLevelSlug: true,
  parentDepartmentId: true,
  departmentName: false,
  departmentNumber: false,
  streetAddress: false,
  city: false,
  zip: false,
  country: false,
  contactPersonId: false,
  supportsKep: false,
};

export interface AddDepartmentPageProps {
  embedded?: boolean;
  initialPlacement?: Pick<DepartmentFormData, 'organizationId' | 'parentDepartmentId'>;
  onCreated?: () => void;
  onComplete?: () => void;
  onClose?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onBusyChange?: (busy: boolean) => void;
}

/** Creates a department in a route or in the department-list overlay. */
export default function AddDepartmentPage({
  embedded = false,
  initialPlacement,
  onCreated,
  onComplete,
  onClose,
  onDirtyChange,
  onBusyChange,
}: AddDepartmentPageProps = {}) {
  const departmentText = useDepartmentText();
  const currentDepartmentText = useCurrentDepartmentText();
  const navigate = useNavigate();
  const { getAction } = useApi();
  const [searchParams] = useSearchParams();
  const [repeatPreference, , updateRepeatPreference] = useLocalStorage('objekto.departments.create-another', {
    enabled: false,
  });
  const [lockedFields, , updateLockedFields] = useLocalStorage<DepartmentRepeatFieldLocks>(
    'objekto.departments.create-another-locks',
    () => ({ ...initialFieldLocks })
  );
  const liveLockedFields = useRef(lockedFields);
  liveLockedFields.current = lockedFields;
  const [formGeneration, setFormGeneration] = useState(0);
  const formRef = useRef<DepartmentFormHandle>(null);
  const [initialValues, setInitialValues] = useState<DepartmentFormData>(() => ({
    ...departmentFormInitialValues,
    organizationId: initialPlacement?.organizationId ?? searchParams.get('organizationId') ?? '',
    parentDepartmentId: initialPlacement?.parentDepartmentId ?? searchParams.get('parentDepartmentId') ?? '',
  }));

  /** Persists the object and applies the selected post-create behavior. */
  async function onSubmit(values: DepartmentFormData) {
    const {
      departmentName,
      departmentNumber,
      streetAddress,
      city,
      zip,
      country,
      organizationId,
      contactPersonId,
      supportsKep,
      parentDepartmentId,
    } = values;

    try {
      const response = await getAction('DepartmentCreate')({
        payload: {
          departmentName,
          departmentNumber: departmentNumber ?? undefined,
          streetAddress,
          city,
          zip,
          country,
          organizationId: Number(organizationId),
          contactPersonId: contactPersonId || contactPersonId === '' ? undefined : Number(contactPersonId),
          supportsKep,
          parentDepartmentId: parentDepartmentId ? Number(parentDepartmentId) : undefined,
        },
      });

      notifications.show({
        title: currentDepartmentText('Položka úrovne bola vytvorená', 'Stredisko bolo vytvorené'),
        message: currentDepartmentText(
          `Položka úrovne ${departmentName} bola úspešne vytvorená.`,
          `Stredisko ${departmentName} bolo úspešne vytvorené.`
        ),
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      onCreated?.();

      formRef.current?.markClean();

      if (repeatPreference.enabled) {
        setInitialValues({
          ...departmentFormInitialValues,
          ...Object.fromEntries(
            (Object.keys(initialFieldLocks) as DepartmentRepeatField[])
              .filter((field) => liveLockedFields.current[field] === true)
              .map((field) => [field, values[field]])
          ),
        });
        setFormGeneration((generation) => generation + 1);
      } else if (embedded) {
        if (onComplete) onComplete();
        else onClose?.();
      } else if (searchParams.get('presentation') === 'new-tab' || DEPARTMENT_REDIRECT_AFTER_SAVE === 'same') {
        navigate(EDIT_DEPARTMENT_PAGE_PATH.insert({ departmentId: response.departmentId }));
      } else if (DEPARTMENT_REDIRECT_AFTER_SAVE === 'list') {
        navigate(DEPARTMENTS_PAGE_PATH.original);
      }
    } catch (error) {
      panic(error as Error);
    }
  }

  const form = (
    <DepartmentForm
      ref={formRef}
      key={formGeneration}
      onSubmit={onSubmit}
      initialValues={initialValues}
      primaryButtonText="Vytvoriť"
      primaryIcon={<IconPlus stroke="1.5" height={24} width={24} />}
      readOnly={{ people: true }}
      embedded={embedded}
      selectWithinPortal={!embedded}
      inferHierarchyLevelFromParent={formGeneration === 0 || lockedFields.hierarchyLevelSlug}
      repeatCreationLocks={
        repeatPreference.enabled
          ? {
              lockedFields,
              onToggle: (field) => updateLockedFields({ [field]: !lockedFields[field] }),
            }
          : undefined
      }
      footerRightSide={
        <Checkbox
          label="Po vytvorení pridať ďalší"
          checked={repeatPreference.enabled}
          onChange={(event) => updateRepeatPreference({ enabled: event.currentTarget.checked })}
        />
      }
      onSecondaryClick={embedded ? onClose : undefined}
      onDirtyChange={onDirtyChange}
      onBusyChange={onBusyChange}
    />
  );

  if (embedded) return form;

  return (
    <DashboardLayout
      title={departmentText('Pridanie novej položky úrovne', 'Pridanie nového strediska')}
      breadcrumbs={[
        { title: departmentText('Položky úrovne', 'Strediská'), link: DEPARTMENTS_PAGE_PATH.original },
        { title: departmentText('Pridanie novej položky úrovne', 'Pridanie nového strediska') },
      ]}
    >
      {form}
    </DashboardLayout>
  );
}
